import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InvoiceSearch from "./InvoiceProduct/InvoiceSearch";
import InvoiceProductForm from "./InvoiceProduct/InvoiceProductForm";
import RetentionModal, { RET_IR_OPTIONS, RET_IVA_OPTIONS } from "./InvoiceRetention/Retention";
import InvoiceTotal from "./InvoiceTotal";

export default function InfoProduct({
  onProductDataChange,
  rows,
  setRows,
  descripcion: descripcionProp,
  setDescripcion: setDescripcionProp,
}) {
  const cbRef = useRef(onProductDataChange);
  useEffect(() => {
    cbRef.current = onProductDataChange;
  }, [onProductDataChange]);

  const searchRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalIR, setModalIR] = useState({ open: false, rowIdx: null });
  const [modalIVA, setModalIVA] = useState({ open: false, rowIdx: null });

  const [descripcionLocal, setDescripcionLocal] = useState("");
  const descripcion = descripcionProp ?? descripcionLocal;
  const setDescripcion = setDescripcionProp ?? setDescripcionLocal;

  const safeRows = Array.isArray(rows) ? rows : [];

  const sanitizeDecimalInput = (s) => String(s ?? "").replace(/,/g, ".");

  const round2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round((x + 1e-10) * 100) / 100;
  };

  const toNum = (v) => {
    const n = Number(sanitizeDecimalInput(v));
    return Number.isFinite(n) ? n : 0;
  };

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : parseInt(r.cantidad, 10) || 0;
    const priceRaw = r.pvp === "" ? 0 : parseFloat(sanitizeDecimalInput(r.pvp)) || 0;
    const price = round2(priceRaw);
    const base = round2(price * qty);

    const mode = r.descuentoModo === "val" ? "val" : "pct";
    const pctRaw = r.descuentoPct === "" ? 0 : parseFloat(sanitizeDecimalInput(r.descuentoPct)) || 0;
    const valRaw = r.descuentoValor === "" ? 0 : parseFloat(sanitizeDecimalInput(r.descuentoValor)) || 0;

    const pct = toNum(pctRaw);
    const val = toNum(valRaw);

    const descCalc = mode === "val" ? clamp(val, 0, base) : clamp(base * (pct / 100), 0, base);
    const descTotal = round2(descCalc);
    const subtotal = round2(base - descTotal);

    return { descTotal, subtotal, base };
  };

  const totals = useMemo(() => {
    return safeRows.reduce(
      (acc, r) => {
        const { descTotal, subtotal } = calcRow(r);
        if (r.iva_porcentaje === 15) acc.sub15 = round2(acc.sub15 + subtotal);
        else if (r.iva_porcentaje === 5) acc.sub5 = round2(acc.sub5 + subtotal);
        else acc.sub0 = round2(acc.sub0 + subtotal);
        acc.descuento = round2(acc.descuento + descTotal);
        return acc;
      },
      { sub15: 0, sub5: 0, sub0: 0, descuento: 0 }
    );
  }, [safeRows]);

  const iva15 = useMemo(() => round2(totals.sub15 * 0.15), [totals.sub15]);
  const iva5 = useMemo(() => round2(totals.sub5 * 0.05), [totals.sub5]);
  const total = useMemo(() => round2(totals.sub15 + totals.sub5 + totals.sub0 + iva15 + iva5), [
    totals.sub15,
    totals.sub5,
    totals.sub0,
    iva15,
    iva5,
  ]);

  const productosPayload = useMemo(() => {
    return safeRows.map((r) => ({
      codigo_principal: r.codigo_principal || "",
      codigo_auxiliar: r.codigo_auxiliar || "",
      descripcion: r.descripcion || "",
      detalles: r.detalles || "",
      descripcion_full: r.descripcion_full || "",
      cantidad: r.cantidad ?? "",
      pvp: r.pvp ?? "",
      iva_porcentaje: r.iva_porcentaje ?? 0,
      descuentoModo: r.descuentoModo || "pct",
      descuentoPct: r.descuentoPct ?? "",
      descuentoValor: r.descuentoValor ?? "",
      ret_ir: r.ret_ir || "",
      ret_iva: r.ret_iva || "",
      retenciones: [
        ...(String(r.ret_ir || "").trim()
          ? [{ codigo: "1", codigo_retencion: String(r.ret_ir).trim() }]
          : []),
        ...(String(r.ret_iva || "").trim()
          ? [{ codigo: "2", codigo_retencion: String(r.ret_iva).trim() }]
          : []),
      ],
    }));
  }, [safeRows]);

  const retencionesResumen = useMemo(() => {
    const items = safeRows.map((r, idx) => ({
      idx,
      ret_ir: r.ret_ir || "",
      ret_iva: r.ret_iva || "",
    }));
    const ir_codigos = Array.from(new Set(items.map((x) => x.ret_ir).filter((x) => String(x).trim() !== "")));
    const iva_codigos = Array.from(new Set(items.map((x) => x.ret_iva).filter((x) => String(x).trim() !== "")));
    return { items, ir_codigos, iva_codigos };
  }, [safeRows]);

  useEffect(() => {
    cbRef.current?.({
      productos: productosPayload,
      productos_raw: safeRows,
      retenciones: retencionesResumen,
      subtotal15: totals.sub15,
      subtotal5: totals.sub5,
      subtotal0: totals.sub0,
      descuento_total: totals.descuento,
      iva15,
      iva5,
      ice: 0,
      total,
      descripcion,
      resumen_totales: {
        sub15: totals.sub15,
        sub5: totals.sub5,
        sub0: totals.sub0,
        descuento: totals.descuento,
        iva15,
        iva5,
        ice: 0,
        total,
      },
    });
  }, [
    productosPayload,
    safeRows,
    retencionesResumen,
    totals.sub15,
    totals.sub5,
    totals.sub0,
    totals.descuento,
    iva15,
    iva5,
    total,
    descripcion,
  ]);

  const handleAdd = ({ pvp, iva_porcentaje, detalles, descuentoModo, descuentoPct, descuentoValor }) => {
    if (!selectedProduct) {
      setErrorMessage("Debe seleccionar un producto.");
      return;
    }

    const baseName = (selectedProduct.name || "").split(" (")[0];
    const codePrincipal = selectedProduct.code || "";
    const codeAux = selectedProduct.codigo_auxiliar || "";
    const variantLabel = selectedProduct.variant_type && selectedProduct.value ? selectedProduct.value : "";
    const det = detalles || variantLabel;

    const price = parseFloat(sanitizeDecimalInput(pvp));
    if (isNaN(price)) {
      setErrorMessage("Precio inválido.");
      return;
    }

    const descripcionFull =
      `${codePrincipal || ""}${codeAux ? ` - ${codeAux}` : ""}`.trim() +
      ` - ${baseName || ""}` +
      (det ? ` - ${det}` : "");

    const modo = descuentoModo === "val" ? "val" : "pct";
    const pctNum = toNum(descuentoPct);
    const valNum = toNum(descuentoValor);

    const pct = String(pctNum).trim() === "" ? "0" : String(pctNum);
    const val = String(valNum).trim() === "" ? "0.00" : String(round2(valNum).toFixed(2));

    setRows([
      ...safeRows,
      {
        codigo_principal: codePrincipal,
        codigo_auxiliar: codeAux,
        cantidad: "1",
        descripcion: baseName,
        detalles: det,
        descripcion_full: descripcionFull,
        pvp: round2(price).toFixed(2),
        iva_porcentaje,
        descuentoModo: modo,
        descuentoPct: pct,
        descuentoValor: val,
        ret_ir: "",
        ret_iva: "",
      },
    ]);

    setSelectedProduct(null);
    searchRef.current?.clear();
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows(safeRows.filter((_, i) => i !== idx));
  };

  const setRowDiscountFromPct = (r, pctStr) => {
    const qty = parseInt(r.cantidad, 10) || 0;
    const price = round2(parseFloat(sanitizeDecimalInput(r.pvp)) || 0);
    const base = round2(price * qty);
    const pct = toNum(pctStr);
    const val = round2(clamp(base * (pct / 100), 0, base));
    return { ...r, descuentoModo: "pct", descuentoPct: pctStr, descuentoValor: val.toFixed(2) };
  };

  const setRowDiscountFromVal = (r, valStr) => {
    const qty = parseInt(r.cantidad, 10) || 0;
    const price = round2(parseFloat(sanitizeDecimalInput(r.pvp)) || 0);
    const base = round2(price * qty);
    const val = round2(clamp(toNum(valStr), 0, base));
    const pct = base > 0 ? (val / base) * 100 : 0;
    return {
      ...r,
      descuentoModo: "val",
      descuentoValor: valStr,
      descuentoPct: pct > 0 ? round2(pct).toFixed(2) : "0",
    };
  };

  const recalcDiscountOnBaseChange = (r) => {
    const mode = r.descuentoModo === "val" ? "val" : "pct";
    if (mode === "val") return setRowDiscountFromVal(r, r.descuentoValor ?? "0.00");
    return setRowDiscountFromPct(r, r.descuentoPct ?? "0");
  };

  const handleFieldChange = (idx, field, value) => {
    let v = value;

    if (field === "pvp" || field === "descuentoPct" || field === "descuentoValor") {
      v = sanitizeDecimalInput(v);
    }

    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;

        if (field === "cantidad") {
          if (v === "" || /^\d*$/.test(v)) {
            const next = { ...r, cantidad: v };
            return recalcDiscountOnBaseChange(next);
          }
          return r;
        }

        if (field === "pvp") {
          const next = { ...r, pvp: v === "" ? "" : v };
          return recalcDiscountOnBaseChange(next);
        }

        if (field === "descuentoPct") {
          if (v === "" || /^\d*\.?\d*$/.test(v)) return setRowDiscountFromPct(r, v === "" ? "" : v);
          return r;
        }

        if (field === "descuentoValor") {
          if (v === "" || /^\d*\.?\d*$/.test(v)) return setRowDiscountFromVal(r, v === "" ? "" : v);
          return r;
        }

        if (field === "iva_porcentaje") return { ...r, iva_porcentaje: v };
        if (field === "ret_ir" || field === "ret_iva") return { ...r, [field]: v };

        return { ...r, [field]: v };
      })
    );
  };

  const pickRetIR = (it) => {
    if (modalIR.rowIdx === null) return;
    handleFieldChange(modalIR.rowIdx, "ret_ir", it.codigo);
    setModalIR({ open: false, rowIdx: null });
  };

  const pickRetIVA = (it) => {
    if (modalIVA.rowIdx === null) return;
    handleFieldChange(modalIVA.rowIdx, "ret_iva", it.codigo);
    setModalIVA({ open: false, rowIdx: null });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2}>
        <InvoiceSearch ref={searchRef} onSelect={setSelectedProduct} />
        <InvoiceProductForm producto={selectedProduct} onAdd={handleAdd} />
      </Grid>

      <RetentionModal
        open={modalIR.open}
        onClose={() => setModalIR({ open: false, rowIdx: null })}
        title="Seleccionar tipo de retención"
        items={RET_IR_OPTIONS}
        onPick={pickRetIR}
      />

      <RetentionModal
        open={modalIVA.open}
        onClose={() => setModalIVA({ open: false, rowIdx: null })}
        title="Seleccionar tipo de retención"
        items={RET_IVA_OPTIONS}
        onPick={pickRetIVA}
      />

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Cant.</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Precio U.</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IR</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IVA</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((r, i) => {
              const { descTotal, subtotal } = calcRow(r);
              return (
                <TableRow key={i}>
                  <TableCell>
                    <TextField
                      value={r.cantidad}
                      onChange={(e) => handleFieldChange(i, "cantidad", e.target.value)}
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 0 }}>
                    <TextField
                      value={r.descripcion_full || ""}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.pvp}
                      onInput={(e) => {
                        const next = sanitizeDecimalInput(e.target.value);
                        if (next !== e.target.value) e.target.value = next;
                      }}
                      onChange={(e) => handleFieldChange(i, "pvp", e.target.value)}
                      type="text"
                      sx={{ width: 100 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onChange={(e) => handleFieldChange(i, "iva_porcentaje", e.target.value)}
                      sx={{ width: 90 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct ?? ""}
                      onInput={(e) => {
                        const next = sanitizeDecimalInput(e.target.value);
                        if (next !== e.target.value) e.target.value = next;
                      }}
                      onChange={(e) => handleFieldChange(i, "descuentoPct", e.target.value)}
                      sx={{ width: 70 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${round2(descTotal).toFixed(2)}</TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <TextField
                        value={r.ret_ir || ""}
                        onChange={(e) => handleFieldChange(i, "ret_ir", e.target.value)}
                        sx={{ width: 80, maxWidth: 80 }}
                        inputProps={{ maxLength: 50 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setModalIR({ open: true, rowIdx: i })}
                        sx={{
                          bgcolor: "#1976d2",
                          color: "#fff",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "#1565c0" },
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <TextField
                        value={r.ret_iva || ""}
                        onChange={(e) => handleFieldChange(i, "ret_iva", e.target.value)}
                        sx={{ width: 80, maxWidth: 80 }}
                        inputProps={{ maxLength: 50 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setModalIVA({ open: true, rowIdx: i })}
                        sx={{
                          bgcolor: "#1976d2",
                          color: "#fff",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "#1565c0" },
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>${round2(subtotal).toFixed(2)}</Typography>
                      <IconButton onClick={() => handleDelete(i)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
